import React, { useState } from "react";
import { Header, Footer } from "../components/blocksafu-ui";
import { useAppKitAccount } from "@reown/appkit/react";
import { useMigrationContract } from "../hooks/useMigration";

export default function Migration() {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { address, isConnected } = useAppKitAccount();
  const { 
    migrationData, 
    isLoading, 
    error, 
    migrate, 
    isMigrationAvailable 
  } = useMigrationContract();

  const handleMigrate = async () => {
    const success = await migrate();
    if (success) {
      setShowConfirmModal(false);
    }
  };

  const formatNumber = (value) => {
    return parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6
    });
  };

  if (!isConnected) {
    return (
      <>
        <Header />
        <section className="min-h-screen px-4 py-16 bg-gradient-to-br from-gray-50 to-gray-100 md:px-12 lg:px-28 dark:from-grayc-950 dark:to-grayc-900 font-body">
          <div className="max-w-4xl mx-auto">
            <div className="text-center">
              <h1 className="mb-4 text-3xl font-bold text-gray-900 dark:text-white">
                Token Migration
              </h1>
              <p className="mb-8 text-gray-600 dark:text-gray-300">
                Please connect your wallet to access the migration tool
              </p>
              <div className="p-8 bg-white rounded-lg shadow-lg dark:bg-grayc-800">
                <p className="text-lg text-gray-500 dark:text-gray-400">
                  Connect your wallet to check your V1 token balances and migrate to V2
                </p>
              </div>
            </div>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      
      <section className="min-h-screen px-4 py-16 bg-gradient-to-br from-gray-50 to-gray-100 md:px-12 lg:px-28 dark:from-grayc-950 dark:to-grayc-900 font-body">
        <div className="max-w-4xl mx-auto">
          
          {/* Header */}
          <div className="mb-8 text-center">
            <h1 className="mb-4 text-3xl font-bold text-gray-900 dark:text-white">
              Token Migration V1 → V2
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              Migrate your V1 tokens and staking positions to the new V2 contract
            </p>
          </div>

          {/* Migration Status */}
          {migrationData.isMigrated ? (
            <div className="p-6 mb-8 bg-green-100 border border-green-200 rounded-lg dark:bg-green-900/20 dark:border-green-800">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                    Migration Complete!
                  </h3>
                  <div className="mt-2 text-sm text-green-700 dark:text-green-300">
                    <p>Your tokens have been successfully migrated to V2.</p>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="grid gap-6 md:grid-cols-2">
            
            {/* V1 Balance Summary */}
            <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-grayc-800">
              <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
                Your V1 Holdings
              </h2>
              
              {isLoading ? (
                <div className="space-y-3">
                  <div className="w-full h-4 bg-gray-200 rounded animate-pulse dark:bg-gray-700"></div>
                  <div className="w-3/4 h-4 bg-gray-200 rounded animate-pulse dark:bg-gray-700"></div>
                  <div className="w-1/2 h-4 bg-gray-200 rounded animate-pulse dark:bg-gray-700"></div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Token Balance:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatNumber(migrationData.balanceTokenV1)} FYT
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Staking (Compound):</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatNumber(migrationData.balanceStakingCompoundV1)} FYT
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Staking (Manual):</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatNumber(migrationData.balanceStakingManualV1)} FYT
                    </span>
                  </div>
                  <hr className="border-gray-200 dark:border-gray-700" />
                  <div className="flex justify-between text-lg font-semibold">
                    <span className="text-gray-900 dark:text-white">Total V1:</span>
                    <span className="text-blue-600 dark:text-blue-400">
                      {formatNumber(migrationData.totalBalance)} FYT
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Migration Action */}
            <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-grayc-800">
              <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
                Migration Action
              </h2>
              
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/20 dark:border-blue-800">
                  <h3 className="font-medium text-blue-800 dark:text-blue-200">
                    Migration Process:
                  </h3>
                  <ul className="mt-2 text-sm text-blue-700 dark:text-blue-300 list-disc list-inside">
                    <li>All V1 tokens will be converted 1:1 to V2</li>
                    <li>Staking positions will be preserved</li>
                    <li>Migration is irreversible</li>
                    <li>No fees for migration</li>
                  </ul>
                </div>
                
                {parseFloat(migrationData.totalBalance) > 0 && !migrationData.isMigrated ? (
                  <button
                    onClick={() => setShowConfirmModal(true)}
                    disabled={isLoading}
                    className="w-full px-6 py-3 font-medium text-white transition-colors bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? "Processing..." : "Migrate to V2"}
                  </button>
                ) : (
                  <div className="p-4 text-center text-gray-500 bg-gray-100 rounded-lg dark:bg-gray-700 dark:text-gray-400">
                    {migrationData.isMigrated ? "Already Migrated" : "No V1 tokens to migrate"}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Migration Stats */}
          <div className="p-6 mt-8 bg-white rounded-lg shadow-lg dark:bg-grayc-800">
            <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
              Migration Statistics
            </h2>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="p-4 bg-gray-50 rounded-lg dark:bg-gray-700">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatNumber(migrationData.totalTokenMigrated)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Total Tokens Migrated
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg dark:bg-gray-700">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  1:1
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Migration Ratio
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg dark:bg-gray-700">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  $0
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Migration Fee
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="max-w-md p-6 mx-4 bg-white rounded-lg shadow-xl dark:bg-grayc-800">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Confirm Migration
            </h3>
            <p className="mb-6 text-gray-600 dark:text-gray-300">
              You are about to migrate {formatNumber(migrationData.totalBalance)} FYT from V1 to V2. 
              This action cannot be undone.
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={handleMigrate}
                disabled={isLoading}
                className="flex-1 px-4 py-2 font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? "Processing..." : "Confirm Migration"}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </>
  );
}